import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import { FilterOption } from 'models/Filters';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import { getSortedOptionsFromStrings } from 'utils';
import { InvitedBroker } from '../../../models/InvitedBroker';
import {
  ACCEPTED_FILTER_KEY,
  EMAIL_FILTER_KEY,
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  NAME_FILTER_KEY,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from '../components/BrokerListTableCells/data';
import useMultiSort from 'hooks/useMultiSort';

export default function useBrokerListFilters() {
  const { t } = useTranslation();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const acceptedFilterOption = useMemo<FilterOption>(
    () => ({
      value: ACCEPTED_FILTER_KEY,
      label: t('accepted'),
      shouldSortOptions: false,
      options: [
        { value: 'true', label: t('accepted') },
        { value: 'false', label: t('notAcceptedYet') },
      ],
    }),
    [t],
  );

  const getNameOptions = useCallback(async (searchString: string) => {
    const { data } = await api.portfolio.admin.getBrokerNameOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const getEmailOptions = useCallback(async (searchString: string) => {
    const { data } = await api.portfolio.admin.getBrokerEmailOptions(
      searchString,
    );

    return getSortedOptionsFromStrings(data);
  }, []);

  const nameFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('name'),
        NAME_FILTER_KEY,
        NAME_FILTER_KEY,
        getNameOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getNameOptions, t],
  );

  const emailFilterOption = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('email'),
        EMAIL_FILTER_KEY,
        EMAIL_FILTER_KEY,
        getEmailOptions,
      ) as FilterOption,
    [getMultiSelectSearchableFilter, getEmailOptions, t],
  );

  const filterOptions = useMemo(
    () => [nameFilterOption, emailFilterOption, acceptedFilterOption],
    [nameFilterOption, emailFilterOption, acceptedFilterOption],
  );

  const {
    multiSortValues: translatedSortOptions,
    multiSortDirectionTranslation,
  } = useMultiSort(
    TABLE_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () =>
      ({
        firstName: {
          options: nameFilterOption,
        },
        email: {
          options: emailFilterOption,
        },
        isBrokerAccepted: {
          options: acceptedFilterOption,
          sortDirectionTranslation:
            multiSortDirectionTranslation[ACCEPTED_FILTER_KEY],
        },
      } as Partial<Record<keyof InvitedBroker, ColumnFilterOption>>),
    [
      nameFilterOption,
      emailFilterOption,
      acceptedFilterOption,
      multiSortDirectionTranslation,
    ],
  );

  return {
    filterOptions,
    translatedSortOptions,
    multiSortDirectionTranslation,
    columnFilterOptions,
  };
}
